/* ==========================================================================
   UltraWheels — global.js
   Drawers, quantity inputs, facet forms, small UI helpers.
   ========================================================================== */

(function () {
  'use strict';

  /* --- Drawer handling ---------------------------------------------------- */

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^='-']), input:not([type='hidden']), select, textarea"
      )
    );
  }

  window.openDrawer = function (id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    const focusable = getFocusableElements(drawer);
    if (focusable.length) focusable[0].focus();
  };

  window.closeDrawer = function (id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  };

  window.closeAllDrawers = function () {
    document.querySelectorAll('.drawer.is-open').forEach((drawer) => {
      window.closeDrawer(drawer.id);
    });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-target]');
    if (opener) {
      event.preventDefault();
      window.openDrawer(opener.dataset.drawerTarget);
      return;
    }

    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      const drawer = closer.closest('.drawer');
      if (drawer) window.closeDrawer(drawer.id);
      return;
    }

    if (event.target.classList && event.target.classList.contains('drawer__overlay')) {
      const drawer = event.target.closest('.drawer');
      if (drawer) window.closeDrawer(drawer.id);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') window.closeAllDrawers();
  });

  /* --- Quantity input ------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        event.preventDefault();
        const previousValue = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        if (button.name === 'minus') this.input.stepDown();
        if (previousValue !== this.input.value) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* --- Facet form (auto-submit filters & sorting) --------------------------- */

  class FacetForm extends HTMLElement {
    constructor() {
      super();
      this.debounceTimer = null;
      // Listen on the element itself so controls linked via the form="" attribute
      // (e.g. the sort select in the toolbar) also trigger a submit.
      this.addEventListener('change', (event) => {
        const form = document.getElementById(this.dataset.formId) || this.querySelector('form');
        if (!form) return;
        if (event.target.matches('input[type="number"]')) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => form.submit(), 600);
        } else {
          form.submit();
        }
      });
    }
  }
  customElements.define('facet-form', FacetForm);

  /* --- Snap slider (hero, reviews) -------------------------------------------- */

  class SnapSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.page = 0;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.sliderDot)));
      });

      let scrollTimer = null;
      this.track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this.syncFromScroll(), 80);
      });

      window.addEventListener('resize', () => this.refresh());
      this.refresh();

      if (this.dataset.autoplay) {
        const delay = Number(this.dataset.autoplay) || 6000;
        this.startAutoplay(delay);
        this.addEventListener('pointerenter', () => this.stopAutoplay());
        this.addEventListener('pointerleave', () => this.startAutoplay(delay));
        this.addEventListener('focusin', () => this.stopAutoplay());
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    get step() {
      const slide = this.slides[0];
      if (!slide) return 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return slide.offsetWidth + gap;
    }

    get perView() {
      return Math.max(1, Math.round(this.track.clientWidth / this.step));
    }

    get pageCount() {
      return Math.max(1, Math.ceil(this.slides.length / this.perView));
    }

    /* One dot per page (group of visible slides), not per slide */
    refresh() {
      const pages = this.pageCount;
      this.dots.forEach((dot, i) => {
        dot.hidden = i >= pages;
      });
      this.syncFromScroll();
    }

    startAutoplay(delay) {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.goTo((this.page + 1) % this.pageCount);
      }, delay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) clearInterval(this.autoplayTimer);
    }

    goTo(page) {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const target = Math.min(maxScroll, page * this.perView * this.step);
      this.page = page;
      this.track.scrollTo({ left: target, behavior: 'smooth' });
      this.updateDots();
    }

    syncFromScroll() {
      const pageWidth = this.perView * this.step;
      this.page = Math.min(this.pageCount - 1, Math.round(this.track.scrollLeft / pageWidth));
      this.updateDots();
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        if (i === this.page) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }
  customElements.define('snap-slider', SnapSlider);

  /* --- Product carousel arrows ------------------------------------------------ */

  // Arrows only show while sliding that direction is possible. Re-runnable:
  // related-products injects its carousel async and calls this again.
  const EDGE = 24;

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((wrapper) => {
      if (wrapper.dataset.carouselInit) return;
      wrapper.dataset.carouselInit = 'true';

      const track = wrapper.querySelector('[data-carousel-track]');
      const prev = wrapper.querySelector('[data-carousel-prev]');
      const next = wrapper.querySelector('[data-carousel-next]');
      if (!track || (!prev && !next)) return;

      const animateScrollTo = (target) => {
        const start = track.scrollLeft;
        const change = target - start;
        if (Math.abs(change) < 1) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          track.scrollLeft = target;
          return;
        }

        // Scroll-snap fights per-frame scrollLeft updates and makes the
        // animation look like a jump — disable it while animating.
        track.style.scrollSnapType = 'none';
        const animId = (track._animId = (track._animId || 0) + 1);
        const duration = 350;
        let startTime = null;
        const tick = (time) => {
          if (track._animId !== animId) return;
          if (startTime === null) startTime = time;
          const progress = Math.min((time - startTime) / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3);
          track.scrollLeft = start + change * ease;
          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            track.style.scrollSnapType = '';
          }
        };
        requestAnimationFrame(tick);
      };

      const scrollByCard = (direction) => {
        const card = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 20;
        const step = card ? card.offsetWidth + gap : track.clientWidth;
        const maxScroll = track.scrollWidth - track.clientWidth;
        const target = Math.max(0, Math.min(maxScroll, track.scrollLeft + direction * step));
        animateScrollTo(target);
      };

      if (prev) prev.addEventListener('click', () => scrollByCard(-1));
      if (next) next.addEventListener('click', () => scrollByCard(1));

      const progress = wrapper.querySelector('[data-carousel-progress]');
      const footer = wrapper.querySelector('.carousel-footer');

      const updateUI = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        const noOverflow = maxScroll <= EDGE;
        if (footer) footer.style.display = noOverflow ? 'none' : '';
        if (prev) prev.disabled = noOverflow || track.scrollLeft <= EDGE;
        if (next) next.disabled = noOverflow || track.scrollLeft >= maxScroll - EDGE;
        if (progress && track.scrollWidth > 0) {
          const fraction = (track.scrollLeft + track.clientWidth) / track.scrollWidth;
          progress.style.width = `${Math.min(100, Math.max(0, fraction * 100))}%`;
        }
      };
      track.addEventListener('scroll', updateUI, { passive: true });
      window.addEventListener('resize', updateUI);
      updateUI();
    });
  }
  window.initCarousels = initCarousels;
  initCarousels();

  /* --- Dual-handle price range slider ------------------------------------------ */

  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.minSlider = this.querySelector('[data-range-min]');
      this.maxSlider = this.querySelector('[data-range-max]');
      this.fill = this.querySelector('[data-range-fill]');
      this.minInput = this.querySelector('[data-price-min]');
      this.maxInput = this.querySelector('[data-price-max]');
      if (!this.minSlider || !this.maxSlider) return;

      this.minSlider.addEventListener('input', () => this.onSlider('min'));
      this.maxSlider.addEventListener('input', () => this.onSlider('max'));
      if (this.minInput) this.minInput.addEventListener('input', () => this.onInput());
      if (this.maxInput) this.maxInput.addEventListener('input', () => this.onInput());
      this.updateFill();
    }

    get bound() {
      return Number(this.maxSlider.max) || 1;
    }

    onSlider(which) {
      let min = Number(this.minSlider.value);
      let max = Number(this.maxSlider.value);
      if (min > max) {
        if (which === 'min') {
          this.maxSlider.value = min;
          max = min;
        } else {
          this.minSlider.value = max;
          min = max;
        }
      }
      // Empty inputs at the outer bounds keep the URL free of redundant params
      if (this.minInput) this.minInput.value = min > 0 ? min : '';
      if (this.maxInput) this.maxInput.value = max < this.bound ? max : '';
      this.updateFill();
    }

    onInput() {
      const min = this.minInput && this.minInput.value !== '' ? Number(this.minInput.value) : 0;
      const max = this.maxInput && this.maxInput.value !== '' ? Number(this.maxInput.value) : this.bound;
      this.minSlider.value = Math.max(0, Math.min(min, this.bound));
      this.maxSlider.value = Math.max(0, Math.min(max, this.bound));
      this.updateFill();
    }

    updateFill() {
      if (!this.fill) return;
      const lo = Number(this.minSlider.value);
      const hi = Number(this.maxSlider.value);
      this.fill.style.left = `${(lo / this.bound) * 100}%`;
      this.fill.style.width = `${(Math.max(0, hi - lo) / this.bound) * 100}%`;
      // Keep the min thumb reachable when both handles sit near the top end
      this.minSlider.style.zIndex = lo > this.bound * 0.9 ? 3 : 2;
    }
  }
  customElements.define('price-range', PriceRange);

  /* --- Collection view toggle (grid / list) ----------------------------------- */

  const VIEW_KEY = 'uw-collection-view';

  function applyProductView(view) {
    const container = document.querySelector('[data-product-view]');
    if (!container) return;
    container.className = view === 'list' ? 'product-list' : 'product-grid product-grid--3';
    document.querySelectorAll('[data-view-toggle]').forEach((button) => {
      const active = button.dataset.viewToggle === view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-view-toggle]');
    if (!toggle) return;
    const view = toggle.dataset.viewToggle;
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch (error) {
      /* private mode */
    }
    applyProductView(view);
  });

  try {
    const savedView = localStorage.getItem(VIEW_KEY);
    if (savedView === 'list') applyProductView('list');
  } catch (error) {
    /* private mode */
  }

  /* --- Mobile facets toggle -------------------------------------------------- */

  const closeFacets = (facets) => {
    facets.classList.remove('is-open');
    document.body.classList.remove('facets-open');
    document.querySelectorAll(`[data-facets-toggle="${facets.id}"]`).forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-facets-toggle]');
    if (toggle) {
      const facets = document.getElementById(toggle.dataset.facetsToggle);
      if (!facets) return;
      const willOpen = !facets.classList.contains('is-open');
      facets.classList.toggle('is-open', willOpen);
      document.body.classList.toggle('facets-open', willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }

    const closer = event.target.closest('[data-facets-close]');
    if (closer) {
      const facets = document.getElementById(closer.dataset.facetsClose);
      if (facets) closeFacets(facets);
      return;
    }

    const open = document.querySelector('.facets.is-open');
    if (open && !event.target.closest('.facets')) closeFacets(open);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.facets.is-open');
    if (open) closeFacets(open);
  });

  /* --- Footer columns collapse on mobile ----------------------------------- */

  /* The markup ships with `open` so the columns read normally on desktop and
     without JS; below the breakpoint they become animated accordions.
     <details> can't be transitioned natively, so the body's height and vertical
     padding are animated by hand and `open` is flipped around the animation. */
  (function footerColumns() {
    const items = document.querySelectorAll('[data-footer-collapsible]');
    if (items.length === 0) return;

    const isDesktop = window.matchMedia('(min-width: 750px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const DURATION = 260;
    const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

    items.forEach((item) => {
      const summary = item.querySelector('summary');
      const body = item.querySelector('.footer__block-body');
      if (!summary || !body) return;

      let animation = null;

      // Height/padding at rest, measured while open.
      function openFrame() {
        const styles = getComputedStyle(body);
        return {
          height: `${body.scrollHeight}px`,
          paddingTop: styles.paddingTop,
          paddingBottom: styles.paddingBottom,
          opacity: 1,
        };
      }

      const closedFrame = { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };

      function run(frames, onDone) {
        if (animation) animation.cancel();
        animation = body.animate(frames, { duration: DURATION, easing: EASING });

        const current = animation;
        // The finished promise beats onfinish here: cancel() rejects it, so a
        // click during an in-flight animation can't run the previous callback.
        current.finished
          .then(() => {
            if (animation === current) animation = null;
            if (onDone) onDone();
          })
          .catch(() => {
            /* cancelled by a newer click — the newer animation owns the state */
          });
      }

      summary.addEventListener('click', (event) => {
        if (isDesktop.matches) return; // a plain heading up there

        event.preventDefault();

        if (reduceMotion.matches) {
          item.open = !item.open;
          return;
        }

        if (item.open) {
          run([openFrame(), closedFrame], () => { item.open = false; });
        } else {
          item.open = true;
          run([closedFrame, openFrame()]);
        }
      });
    });

    function sync() {
      items.forEach((item) => { item.open = isDesktop.matches; });
    }

    sync();
    isDesktop.addEventListener('change', sync);
  })();

  /* --- Language switcher (Google Translate) -------------------------------- */

  class LanguageSwitcher extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-lang-toggle]');
      this.list = this.querySelector('[data-lang-list]');
      if (!this.toggle || !this.list) return;

      this.names = {};
      this.flags = {};
      this.querySelectorAll('[data-lang]').forEach((b) => {
        const code = b.dataset.lang;
        this.names[code] = b.querySelector('span:last-child').textContent.trim();
        this.flags[code] = b.querySelector('.langsel__flag').innerHTML;
        b.addEventListener('click', () => this.choose(code));
      });

      this.toggle.addEventListener('click', () => this.setOpen(this.list.hidden));

      this.onDocClick = (e) => { if (!this.contains(e.target)) this.setOpen(false); };
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.setOpen(false); });

      this.show(this.current());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    setOpen(open) {
      this.list.hidden = !open;
      this.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /* Google stores the active pair in a `googtrans=/from/to` cookie. */
    current() {
      const m = document.cookie.match(/googtrans=\/[^/]+\/([a-z-]+)/i);
      return m ? m[1] : (this.dataset.default || 'nl');
    }

    show(code) {
      if (!this.names[code]) return;
      this.querySelector('[data-lang-label]').textContent = this.names[code];
      this.querySelector('[data-lang-flag]').innerHTML = this.flags[code];
      this.querySelectorAll('[data-lang]').forEach((b) => {
        b.setAttribute('aria-selected', b.dataset.lang === code ? 'true' : 'false');
      });
    }

    choose(code) {
      const from = this.dataset.default || 'nl';
      const value = code === from ? '' : `/${from}/${code}`;
      // Written on both the bare host and the dot-prefixed domain: Google reads
      // whichever the widget was initialised against, and they differ per setup.
      const host = window.location.hostname;
      const opts = `;path=/;max-age=${60 * 60 * 24 * 365}`;
      document.cookie = `googtrans=${value}${opts}`;
      document.cookie = `googtrans=${value}${opts};domain=${host}`;
      document.cookie = `googtrans=${value}${opts};domain=.${host}`;
      window.location.reload();
    }
  }
  customElements.define('language-switcher', LanguageSwitcher);

  /* --- WhatsApp teaser bubble ---------------------------------------------- */

  class WhatsappChat extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-wa-panel]');
      this.toggle = this.querySelector('[data-wa-toggle]');
      this.form = this.querySelector('[data-wa-form]');
      this.input = this.querySelector('[data-wa-input]');
      this.badge = this.querySelector('[data-wa-badge]');
      if (!this.panel || !this.toggle) return;

      this.key = this.dataset.storageKey || 'wa-panel-dismissed';

      this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));

      const closer = this.querySelector('[data-wa-close]');
      if (closer) {
        closer.addEventListener('click', () => {
          this.setOpen(false);
          this.remember();
        });
      }

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.setOpen(false);
          this.toggle.focus();
        }
      };
      document.addEventListener('keydown', this.onKeydown);

      const delay = parseInt(this.dataset.delay, 10) || 0;
      if (delay > 0 && !this.wasDismissed()) {
        this.timer = setTimeout(() => this.setOpen(true, { focus: false }), delay * 1000);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
    }

    get isOpen() {
      return this.classList.contains('wa--open');
    }

    setOpen(open, options) {
      clearTimeout(this.timer);

      if (open) {
        this.panel.hidden = false;
        // Flush layout so the transition has a start state to animate from.
        // Deliberately not requestAnimationFrame: that never fires while the
        // tab is in the background, which left the panel stuck at opacity 0
        // when the auto-open timer ran on a hidden tab.
        void this.panel.offsetHeight;
        this.classList.add('wa--open');
        if (this.badge) this.badge.hidden = true;
        if (this.input && (!options || options.focus !== false)) this.input.focus();
      } else {
        this.classList.remove('wa--open');
        setTimeout(() => { this.panel.hidden = true; }, 200);
      }

      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onSubmit(event) {
      event.preventDefault();
      const typed = this.input ? this.input.value.trim() : '';
      const message = typed || this.dataset.fallback || '';
      const url = `https://wa.me/${this.dataset.number}?text=${encodeURIComponent(message)}`;

      // Opened in a new tab so the shop stays put behind the conversation.
      window.open(url, '_blank', 'noopener');

      if (this.input) this.input.value = '';
      this.setOpen(false);
      this.remember();
    }

    /* Private browsing can make localStorage throw; never let that break the
       chat — worst case the panel opens by itself again next visit. */
    wasDismissed() {
      try {
        return localStorage.getItem(this.key) === '1';
      } catch (error) {
        return false;
      }
    }

    remember() {
      try {
        localStorage.setItem(this.key, '1');
      } catch (error) {
        /* ignore */
      }
    }
  }
  customElements.define('whatsapp-chat', WhatsappChat);

  /* --- Sticky header height ------------------------------------------------ */

  /* Published as --header-height so sticky elements below the header (the
     collection filters) can offset themselves without a magic number. Stays
     unset when the header isn't sticky, so those elements fall back to 0. */
  (function trackHeaderHeight() {
    const header = document.querySelector('.header-wrapper--sticky');
    if (!header) return;

    const publish = () => {
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    };

    publish();
    if (window.ResizeObserver) new ResizeObserver(publish).observe(header);
    else window.addEventListener('resize', publish);
  })();
})();
